import useTlmCart from '~/composable/useTlmCart';
import useCartQueries from '~/composable/useCartQueries';
import useErrorHandler from '~/composable/useErrorHandler';

export default function useCheckoutInfo (appContext) {
  const hubbleClient = appContext.$hubble;
  if (!appContext || !hubbleClient) {
    throw new Error('You must provide the nuxt app context to use this composable');
  }
  const { deviceList, getCartID, subscriptionList, updateCart, cart } = useTlmCart(appContext);
  const { getDeviceWithInstallment } = useCartQueries(appContext, cart, { skipAutoLoad: true });
  const { redirectToErrorPage } = useErrorHandler()

  const fromSubscription = () => {
    const sub = subscriptionList?.value;
    if (sub?.cartItemGroupID == null || sub?.sku == null) {
      return null;
    }
    return { groupID: sub.cartItemGroupID, sku: sub.sku };
  };
  const fromDevice = () => {
    const device = deviceList?.value;
    const groupID = device?.cartItemGroupID;
    const sku = device?.sku ?? device?.cartProduct?.sku;
    if (groupID == null || sku == null) {
      return null;
    }
    return { groupID, sku };
  };
  const fromFirstCartGroup = () => {
    const firstGroup = cart?.value?.cartItemGroupList?.[0];
    const firstItem = firstGroup?.cartItems?.[0];
    const sku = firstItem?.sku ?? firstItem?.cartProduct?.sku;
    if (firstGroup?.id == null || sku == null) {
      return null;
    }
    return { groupID: firstGroup.id, sku };
  };
  const getGroupIdAndSku = () => {
    return fromSubscription() ?? fromDevice() ?? fromFirstCartGroup() ?? null;
  };

  const addNumberInfo = async (numberInfoDTO) => {
    const groupIdAndSku = getGroupIdAndSku();
    if (!groupIdAndSku) {
      return;
    }
    const res = await hubbleClient.checkout.addNumberInfo(getCartID.value, numberInfoDTO, {
      cartID: getCartID.value,
      sku: groupIdAndSku.sku,
      groupID: groupIdAndSku.groupID
    }).catch(() => {
      redirectToErrorPage('cartError')
    });
    updateCart(res.data.responseObject);
    return res;
  }
  const addPermissionInformation = async (permissionInformation) => {
    const groupIdAndSku = getGroupIdAndSku();
    if (!groupIdAndSku) {
      return;
    }
    const actionResponse =
      await hubbleClient.checkout.addPermissionInformation(permissionInformation, getCartID.value, groupIdAndSku.groupID, groupIdAndSku.sku)
    updateCart(actionResponse.data.responseObject);
    return actionResponse;
  }
  const addEsimOnlyDeviceInformation = async (esimOnlyDeviceInfo) => {
    const actionResponse =
      await hubbleClient.checkout.addEsimOnlyDeviceInformation(esimOnlyDeviceInfo, getCartID.value, subscriptionList.value.cartItemGroupID, deviceList.value.sku)
    updateCart(actionResponse.data.responseObject);
    return actionResponse;
  }
  const removeInstallmentFromCart = async () => {
    const cartItemGroupUpdate = {
      cartID: cart.value.id,
      sku: getDeviceWithInstallment.value.sku,
      groupID: getDeviceWithInstallment.value.cartItemGroupID
    }

    const res = await hubbleClient.checkout.removeInstallments(cartItemGroupUpdate);
    updateCart(res.data.responseObject);
    return res
  }
  const addChangeSubscriptionCustomerInfo = async (prepaidAccountID, serviceAccountID) => {
    if (!prepaidAccountID || !serviceAccountID) {
      throw new Error('You must provide prepaidAccountID, serviceAccountID')
    }
    const cartItemGroupUpdate = {
      cartID: cart.value.id,
      sku: subscriptionList.value.sku,
      groupID: subscriptionList.value.cartItemGroupID,
      customFieldValue: [
        {
          name: 'PrepaidAccountId',
          value: prepaidAccountID
        },
        {
          name: 'ServiceAccountId',
          value: serviceAccountID
        }]

    }

    const res = await hubbleClient.checkout.addCustomerChangeSubscriptionInfo(cartItemGroupUpdate).catch(() => {
      redirectToErrorPage('cartError')
    });
    updateCart(res.data.responseObject);
    return res
  }
  const removeChangeSubscriptionCustomerInfo = async () => {
    const cartItemGroupUpdate = {
      cartID: cart.value.id,
      sku: subscriptionList.value.sku,
      groupID: subscriptionList.value.cartItemGroupID
    }

    const res = await hubbleClient.checkout.removeCustomerChangeSubscriptionInfo(cartItemGroupUpdate).catch(() => {
      redirectToErrorPage('cartError')
    });
    updateCart(res.data.responseObject);
    return res
  }
  const removeNumberInfo = async () => {
    const cartItemGroupUpdate = {
      cartID: cart.value.id,
      sku: subscriptionList.value.sku,
      groupID: subscriptionList.value.cartItemGroupID
    }

    const res = await hubbleClient.checkout.removeNumberInfo(cartItemGroupUpdate).catch(() => {
      redirectToErrorPage('cartError')
    });
    updateCart(res.data.responseObject);
    return res
  }

  const addPhoneNumberToAddress = async (phoneNumber) => {
    if (phoneNumber !== null && phoneNumber !== '') {
      const res = await hubbleClient.checkout.addPhoneNumberToAddress(
        cart.value.id,
        phoneNumber
      );
      updateCart(res.data.responseObject);
      return res
    }
    console.error('phoneNumber is either null or empty in addPhoneNumberToAddress')
  }

  const addPhoneNumberAndEmailToAddress = async (phoneNumber, email) => {
    if (phoneNumber !== null && phoneNumber !== '' && email !== null && email !== '') {
      const res = await hubbleClient.checkout.addPhoneNumberAndEmailToAddress(
        cart.value.id,
        phoneNumber,
        email
      );
      updateCart(res.data.responseObject);
      return res
    }
    console.error('phoneNumber or email is either null or empty in addPhoneNumberAndEmailToAddress')
  }

  const addPrepaidEmail = async (emailAddress) => {
    const groupIdAndSku = getGroupIdAndSku();
    if (!groupIdAndSku) {
      return;
    }
    const res = await hubbleClient.checkout.addPrePaidEmail(getCartID.value, emailAddress, {
      cartID: getCartID.value,
      sku: groupIdAndSku.sku,
      groupID: groupIdAndSku.groupID
    });
    updateCart(res.data.responseObject);
    return res;
  }
  const addPurchaseCategory = async () => {
    const res = await hubbleClient.checkout.addPurchaseCategory(getCartID.value);
    updateCart(res.data.responseObject);
    return res;
  }
  const setCustomerType = (type) => {
    appContext.$storage.setLocalStorage('tlmCustomerType', type);
  }
  const getCustomerType = () => {
    return appContext.$storage.getLocalStorage('tlmCustomerType');
  }
  const setCVRNumberInStorage = (cvrNumber) => {
    appContext.$storage.setLocalStorage('tlmCvr', cvrNumber);
  }
  const getCVRNumberInStorage = () => {
    return appContext.$storage.getLocalStorage('tlmCvr');
  }
  const setCustomerInfoInStorage = (info) => {
    appContext.$storage.setLocalStorage('tlmCustomerInfo', info);
  }
  const getCustomerInfoInStorage = () => {
    return appContext.$storage.getLocalStorage('tlmCustomerInfo')
  }
  return {
    setCustomerInfoInStorage,
    getCustomerInfoInStorage,
    setCVRNumberInStorage,
    getCVRNumberInStorage,
    getCustomerType,
    setCustomerType,
    addPhoneNumberAndEmailToAddress,
    addPurchaseCategory,
    addPrepaidEmail,
    addPhoneNumberToAddress,
    removeNumberInfo,
    removeInstallmentFromCart,
    addNumberInfo,
    addPermissionInformation,
    addEsimOnlyDeviceInformation,
    addChangeSubscriptionCustomerInfo,
    removeChangeSubscriptionCustomerInfo
  }
}
